// Settle later — every trade agreed but not fully moved, kept in sight.
//
// Operator, 2 Sept 2026: "LET'S SETTLE THE SETTLE LATER TRADES, AND NEED SOME
// MECHANISM TO DO THAT OFTEN SO THAT IT STAYS VISIBLE."
//
// The unit here is the PARTY, not the deal. A settle-later trade leaves its
// two legs in the party's box (they owe you the one, you hold the other) and
// settlement is ordinary money in / out against that box — merchants run a
// running account across many trades. So each card shows the box first, the
// deals beneath it as the explanation, and the two moves that close it.
// Reading this screen runs the sweep, and the sweep closes a trade LEG BY LEG:
// the take leg when the money came in, the give leg when it went out, either
// leg by netting when the party's box no longer carries that asset — and, for a
// party the operator has marked as carrying a running account (db/0036), a leg
// on which money HAS MOVED, with the remainder riding on their balance. A leg
// nothing has moved on still blocks, marked party or not.
//
// THAT LAST ONE IS THE ONLY WAY A TRADE LEAVES THIS SCREEN STILL OWING
// SOMETHING, so wherever it happens this screen says so and says how much: on
// each swept row, and on the leg itself (legWords prints "INR on account ·
// 15,705 rode"). "Settled" flat would hide a figure the desk still owes;
// "forced" would call ordinary business an override.
//
// Minimised 14 Sept 2026 (sweep §5.15 / item 55): the "awaiting" badge is gone
// (every card here is awaiting), the box reads in the one vocabulary
// (boxWords: "AED · owes you 36,710"), the square line is short, the tooltip
// sentences live in "About this screen" (a tooltip does not exist on a
// phone), and the Mark settled refusal panel opens full width beneath its
// row with only the closed button in the row.
//
// 21 Oct 2026 audit (operator: "I need clean view for operator"; the doors,
// what they prefill and when they are refused are untouched — labels, copy and
// layout only):
//   · a deal row's own Receive / Pay link shows only when it says something the
//     box button above it does not — an open figure that differs from the box
//     (15,705 open of a 91,767 box), or an asset the box has no door for. One
//     open trade for the whole box printed the same two figures four times;
//   · the legs row wraps at the " · " and the reference drops to its own line,
//     instead of squeezing the words on a phone ("USDT 62,449 / to pay");
//   · a trade closed on this read keeps what rode on screen (the 3-4 Sept
//     ruling) and folds its full deal id and "reopen" behind "details";
//   · the cards stop at the ledger block's width on a wide monitor.
//
// 22 Oct 2026 design-system sweep: a failed read is ONE red line (what failed, Try again, the server's sentence a tap away) and
// "Nothing awaiting." the kit's EmptyState; Receive and Pay stand right beside the figure they prefill on a laptop (they sat 1,000 px
// away at the card's edge) and are 44 px, body-size targets on a phone; the legs row gives the reference air. Nothing that posts,
// prefills or refuses changed.
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { errorWords, readWords } from '../lib/errors';
import { Link } from 'react-router-dom';
import { api, refreshMoneyViews, type SettlementParty, type SettlementsResponse, type DealRow } from '../lib/api';
import { ScreenHeader } from '../components/ScreenHeader';
import { SettleButton, legWords } from '../components/SettleButton';
import { Pending, StateLine, EmptyState } from '../components/kit';
import { CopyRef } from '../components/CopyRef';
import { DealRowLine, LEDGER } from '../components/Ledger';
import { BoxWords } from '../components/BoxLines';
import { displayAsset, fmtAmount, fmtRelative } from '../lib/format';
import { partyName, rodeSentence, dustFor, shortReference } from '../lib/naming';

/** WHAT HE PRESSES (design system §1, §4): body size, a 44 px target on a phone, centred. */
const TOUCH = 'text-body py-1.5 px-3 inline-flex items-center justify-center max-lg:min-h-11';

export function Settlements() {
  // LIVE (operator, 3 Sept: "screen not updating instantly"): a receipt posted
  // on another screen or tab must show here without a navigation — refetch on
  // focus and every 15 s; every posting also invalidates this key.
  const q = useQuery({ queryKey: ['settlements'], queryFn: () => api.settlements(), staleTime: 0,
    refetchOnWindowFocus: true, refetchOnMount: 'always', refetchInterval: 15_000 });
  const parties = q.data?.parties ?? [];
  const swept = q.data?.swept ?? [];

  return (
    <div className="space-y-4">
      <ScreenHeader
        title="Settle later"
        subtitle="Trades agreed but not fully moved."
        help={
          <>
            <p>
              A trade closes by itself once each leg has been received, paid, or netted against what the
              party still owes — or, for a party who carries a running account, once money has moved on
              the leg and the rest rides on their balance. Record the money; the trade closes by itself.
            </p>
            <p>
              Receive and Pay on a card prefill the party&rsquo;s whole box. The link on a deal row
              prefills that leg&rsquo;s open figure — use the row&rsquo;s link when a trade is part-paid.
            </p>
            <p>
              Reopen, under details on a closed row, puts a trade the sweep just closed back on this list;
              only real money in or out closes it again. &ldquo;Still to receive&rdquo; / &ldquo;still to
              pay&rdquo; says money is still expected on a leg the arithmetic calls settled — the trade
              reopens, and only a receipt or payment closes it.
            </p>
            <p>
              Mark settled anyway closes a trade with a leg still open and records no ride — the figure is
              written off, not carried. A part-paid leg is either short or the party&rsquo;s own margin;
              which one is the Running account setting on their party page, never a judgement per trade —
              and never on a leg you have held or a trade you have reopened: your word on one trade
              outranks a setting on the party. A trade closed onto a running account says what rode on
              its row.
            </p>
          </>
        }
      >
        {swept.length > 0 && (
          <div className="space-y-1">
            <p className="text-meta text-pos">
              {swept.length} trade{swept.length > 1 ? 's' : ''} closed on this read.
            </p>
            {swept.map((s) => <SweptRow key={s.deal_id} deal={s} />)}
          </div>
        )}
      </ScreenHeader>

      {/* PENDING, not isLoading: a first read that is PAUSED (offline) has no data and is not fetching — it is still waiting, and it is not "Nothing awaiting". */}
      {q.isPending && <Pending />}
      {/* A FAILED READ is never the "Nothing awaiting" below: one red line, Try again, the server's sentence a tap away. */}
      {q.isError && (
        <StateLine
          tone="bad"
          more={readWords(q.error)}
          action={<button type="button" className="btn-link text-body" disabled={q.isFetching} onClick={() => q.refetch()}>{q.isFetching ? 'Trying…' : 'Try again'}</button>}
        >
          Couldn&rsquo;t read settle-later trades.
        </StateLine>
      )}
      {q.isSuccess && parties.length === 0 && <EmptyState>Nothing awaiting.</EmptyState>}

      {/* Capped like every ledger block, so the figures sit near their buttons
          on a wide monitor (21 Oct 2026 audit). */}
      <div className={`space-y-3 ${LEDGER.block}`}>
        {parties.map((p) => <PartyCard key={p.slug} p={p} />)}
      </div>
    </div>
  );
}

/** What the desk would say about the trade, from the customer's side —
 *  "Loki buys 10,000 USDT · pays 36,710 AED". Shared with the party page. */
export function dealWords(d: DealRow): string {
  const who = partyName({ display_name: d.party_name, slug: d.party_slug ?? '' });
  const take = `${fmtAmount(d.amount_take, d.asset_take)} ${displayAsset(d.asset_take)}`;
  const give = `${fmtAmount(d.amount_give, d.asset_give)} ${displayAsset(d.asset_give)}`;
  // customer_buy: the customer takes the desk's give leg and pays the take leg
  return d.direction === 'customer_buy'
    ? `${who} buys ${give} · pays ${take}`
    : `${who} sells ${take} · gets ${give}`;
}

// The deal sentence and the settle slot that used to live here (DealSentence,
// SETTLE_SLOT) are gone: every deal row on the console is components/Ledger's
// DealRowLine (19 Sept 2026), and the button sits in its action seat.

function PartyCard({ p }: { p: SettlementParty }) {
  const name = partyName({ display_name: p.display_name, slug: p.slug });
  const owes = Object.entries(p.box).filter(([a, v]) => v > dustFor(a));
  const held = Object.entries(p.box).filter(([a, v]) => v < -dustFor(a));
  const pq = `party=${encodeURIComponent(p.slug)}`;
  // A DEAL ROW'S OWN LINK SHOWS ONLY WHEN IT SAYS SOMETHING THE BOX BUTTON DOES
  // NOT (21 Oct 2026 audit). A link naming the same asset and the same figure as
  // the box button above it is a second door to the same prefill — one open
  // trade for the whole box printed it four times. Where the leg's open figure
  // differs from the box (a running account: 15,705 open of a 91,767 box), or
  // the box has no door in that direction for that asset, the link stays: it is
  // the one that carries the leg's own figure. A give leg is compared with the
  // held side of the box (−box). Same raw asset code, and the SAME FIGURE AS HE
  // READS IT (fmtAmount) — not "within dust": 10,000.4 USDT in the box and 10,000
  // on the leg are two different prefills, and the leg's exact figure is the one
  // he must be able to post (Astra, 21 Oct).
  const boxDoor = (dir: 'in' | 'out', asset: string, open: number) =>
    (dir === 'in' ? owes : held).some(([a, v]) => a === asset && fmtAmount(dir === 'in' ? v : -v, a) === fmtAmount(open, a));

  return (
    <div className="card p-3 space-y-3">
      <div className="min-w-0">
        <Link to={`/parties/${encodeURIComponent(p.slug)}?entity=dubaiotc`} className="text-body font-medium hover:underline">
          {name}
        </Link>
        <p className="text-meta text-tg-hint">
          {p.deals.length} trade{p.deals.length > 1 ? 's' : ''} open · oldest {fmtRelative(p.oldest)}
        </p>
      </div>

      {/* THE BOX — what actually stands between you, in the one vocabulary
          (boxWords). THE BUTTON NAMES THE FIGURE IT PREFILLS (sweep item 33,
          14 Sept 2026): these links carry the party's WHOLE box, which is
          exactly how 15,705 INR was left behind on 4 Sept — the operator read
          one figure on the deal row and posted another. Each deal row below
          carries its own link for the open leg. */}
      {/* One row per asset on fixed seats: the code, the box in words, the
          door at the right. The door says only its verb — the figure it
          prefills is the one on its own line, and a button that repeated
          it wrapped inside itself at 375px and broke the words beside it
          (20 Sept 2026); the deal row's link beneath still names the
          leg's open figure, which is the one that differs. */}
      <div className="grid gap-1.5 text-body">
        {/* One row per asset on fixed seats: the code, the box in words, the door. A phone: the door at the right edge, a 44 px
            target. A laptop: the door stands BESIDE the figure it prefills (the words' seat is fixed, so the doors line up in a
            column) — at the card's far edge it was a thousand pixels from the number. */}
        {owes.map(([a, v]) => (
          <div key={a} className="grid grid-cols-[2.75rem_minmax(0,1fr)_auto] items-center gap-x-3 lg:grid-cols-[2.75rem_20rem_auto] lg:justify-start lg:gap-x-4">
            <span className="text-meta text-tg-hint">{displayAsset(a)}</span>
            <BoxWords value={v} asset={a} answer />
            <Link className={`btn-secondary ${TOUCH} whitespace-nowrap`} title={`Receive ${fmtAmount(v, a)} ${displayAsset(a)}`} to={`/record?${pq}&dir=in&asset=${a}&amount=${v}`}>
              Receive
            </Link>
          </div>
        ))}
        {held.map(([a, v]) => (
          <div key={a} className="grid grid-cols-[2.75rem_minmax(0,1fr)_auto] items-center gap-x-3 lg:grid-cols-[2.75rem_20rem_auto] lg:justify-start lg:gap-x-4">
            <span className="text-meta text-tg-hint">{displayAsset(a)}</span>
            <BoxWords value={v} asset={a} answer />
            <Link className={`btn-secondary ${TOUCH} whitespace-nowrap`} title={`Pay ${fmtAmount(-v, a)} ${displayAsset(a)}`} to={`/record?${pq}&dir=out&asset=${a}&amount=${-v}`}>
              Pay
            </Link>
          </div>
        ))}
        {owes.length === 0 && held.length === 0 && (
          <p className="text-meta text-tg-hint">Square · open in {p.open_assets.map(displayAsset).join(', ')}</p>
        )}
      </div>

      {/* THE ONE DEAL ROW (components/Ledger DealRowLine — Home, the party
          page and this screen; operator, 19 Sept 2026: "fix the phone layout
          for the settle screen too"): stamp · legs · @ rate, Mark settled in
          the row's action seat, and beneath it the legs' state with the
          row's own Receive/Pay link for each open leg — the open figure
          (15,705), never the box (91,767). The old row was five lines of
          three sizes with the sentence cut short on a phone.

          21 Oct 2026 audit: the link shows only where it differs from the box
          button (boxDoor, above). The legs row wraps — each leg is one
          unbreakable piece, broken only at the " · " between them — and the
          reference takes a line of its own when the two do not fit, where it
          used to squeeze the words on a phone ("USDT 62,449 / to pay"). */}
      <div className="-mx-1 divide-y divide-stroke-soft border-t border-stroke-soft">
        {p.deals.map((d) => {
          const legs = d.legs;
          const receiveLink = !!legs && !legs.take.settled && legs.take.open > 0 && !boxDoor('in', legs.take.asset, legs.take.open);
          const payLink = !!legs && !legs.give.settled && legs.give.open > 0 && !boxDoor('out', legs.give.asset, legs.give.open);
          return (
            <DealRowLine
              key={d.deal_id}
              deal={d}
              hideParty
              hideState
              action={<SettleButton dealId={d.deal_id} partySlug={p.slug} />}
              detail={
                // Two lines at most: the legs' state with the reference at the
                // right; then the row's own link for each open leg that differs
                // from the box.
                <div className="space-y-0.5 text-meta">
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                    {legs && (
                      <span className="min-w-0">
                        <span className="whitespace-nowrap">
                          <span className={legs.take.settled ? 'text-pos' : 'text-warn'}>{legWords(legs.take, 'take')}</span>
                          {legs.take.settled && <HoldLink dealId={d.deal_id} leg="take" />}
                        </span>
                        <span className="text-tg-hint"> · </span>
                        <span className="whitespace-nowrap">
                          <span className={legs.give.settled ? 'text-pos' : 'text-warn'}>{legWords(legs.give, 'give')}</span>
                          {legs.give.settled && <HoldLink dealId={d.deal_id} leg="give" />}
                        </span>
                      </span>
                    )}
                    <CopyRef value={d.deal_id} label={shortReference(d.deal_id)} className="ml-auto shrink-0" />
                  </div>
                  {(receiveLink || payLink) && (
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5">
                      {legs && receiveLink && (
                        <Link className="btn-link text-meta" to={`/record?${pq}&dir=in&asset=${legs.take.asset}&amount=${legs.take.open}`}>
                          Receive {fmtAmount(legs.take.open, legs.take.asset)} {displayAsset(legs.take.asset)} →
                        </Link>
                      )}
                      {legs && payLink && (
                        <Link className="btn-link text-meta" to={`/record?${pq}&dir=out&asset=${legs.give.asset}&amount=${legs.give.open}`}>
                          Pay {fmtAmount(legs.give.open, legs.give.asset)} {displayAsset(legs.give.asset)} →
                        </Link>
                      )}
                    </div>
                  )}
                </div>
              }
            />
          );
        })}
      </div>
    </div>
  );
}

/** THE UNDO for a trade the sweep just closed.
 *
 *  The sweep decides by itself, on every read, so the operator has to be able
 *  to say it got one wrong. Only trades THIS read closed are offered, and that
 *  limit is deliberate. An ordinary instant trade is also written lifecycle
 *  'settled', and nothing on a settled row tells the two apart — so a general
 *  "Reopen" on any settled deal would let one tap turn a finished trade into
 *  an open position. Everything in `swept` was an awaiting settle-later trade
 *  a second ago. */
function SweptRow({ deal }: { deal: SettlementsResponse['swept'][number] }) {
  const qc = useQueryClient();
  const m = useMutation({ mutationFn: () => api.reopenDeal(deal.deal_id), onSuccess: () => refreshMoneyViews(qc) });
  // WHAT RODE, ON THE ROW ITSELF. This trade has just left the list: the deal's
  // note and the sweep's audit row both carry the figure, but neither is on a
  // screen the operator is looking at, and this line is the last moment it can
  // be shown next to the trade it came off.
  const rode = rodeSentence(deal.rode);
  // 21 Oct 2026 audit: the full deal id (DEAL-2026-10-15-Q2L9X) and "reopen"
  // were two long lines above the cards that pushed the money down. The party
  // and WHAT RODE stay on the line — that figure is the money fact (3-4 Sept
  // ruling) — and the id and the undo sit one tap down. A failed reopen is told
  // either way, never folded.
  const [more, setMore] = useState(false);
  return (
    <div className="flex flex-wrap items-baseline gap-x-2 text-meta text-tg-hint">
      <span className="truncate">{partyName({ slug: deal.party_slug })}</span>
      {rode && <span className="text-info">{rode}</span>}
      <button
        type="button"
        aria-expanded={more}
        className="underline decoration-dotted hover:text-tg-text max-lg:p-2 max-lg:-m-2"
        onClick={() => setMore((v) => !v)}
      >
        {more ? 'hide' : 'details'}
      </button>
      {more && (
        <>
          <span className="font-mono">{deal.deal_id}</span>
          {/* A NATIVE CONFIRM (sweep item 33, 14 Sept 2026): this is a one-tap
              write that turns a finished trade back into an open position. The
              same pattern the party page uses for Promote and Running account. */}
          <button
            type="button"
            className="underline decoration-dotted hover:text-tg-text disabled:opacity-50 max-lg:p-2 max-lg:-m-2"
            disabled={m.isPending}
            onClick={() => {
              if (confirm(`Reopen ${deal.deal_id}? Only real money in or out will close it again.`)) m.mutate();
            }}
          >
            {m.isPending ? 'reopening…' : 'reopen'}
          </button>
        </>
      )}
      {m.isError && <span className="text-neg">{errorWords(m.error)}</span>}
    </div>
  );
}

/** "still to receive" / "still to pay" beside a leg the arithmetic calls
 *  settled — the operator's word that money is still expected for it (3 Sept,
 *  HK's coins). A VERB, not a status word: this is a write that reopens the
 *  trade and changes what the sweep counts, and it must never fail in silence. */
function HoldLink({ dealId, leg }: { dealId: string; leg: 'take' | 'give' }) {
  const qc = useQueryClient();
  const m = useMutation({ mutationFn: () => api.holdLeg(dealId, [leg]), onSuccess: () => refreshMoneyViews(qc) });
  const verb = leg === 'take' ? 'Still to receive' : 'Still to pay';
  return (
    <span className="inline-flex flex-col items-start">
      <button type="button" className="ml-1 text-meta text-tg-hint underline decoration-dotted hover:text-tg-text max-lg:p-2 max-lg:-m-2" disabled={m.isPending}
        onClick={() => {
          // The consequence as a confirm (sweep item 33): this reopens the
          // trade and changes what the sweep counts.
          if (confirm(`${verb} on ${dealId}? The trade reopens — only a receipt or payment closes it.`)) m.mutate();
        }}>
        {m.isPending ? 'holding…' : verb.toLowerCase()}
      </button>
      {/* whitespace-normal: the legs row keeps each leg on one line, and a failed
          hold must wrap inside that, never run off a phone's edge. */}
      {m.isError && <span className="ml-1 text-meta text-neg whitespace-normal">{errorWords(m.error)}</span>}
    </span>
  );
}
